import styled from '@emotion/styled';

import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { ResourceCard } from '@/components/common/Card/ResourceCard';
import { HeadingLevel } from '@/components/common/MarkUp';
import { formatDate } from '@/components/common/utils';
import { Section } from '@/components/layout/Section';
import { blocksToText } from '@/lib/sanityUtils';

import { ArticleGridSytem } from './ArticleGridSytem';

import type { LatestPostsDataProps } from '@/components/blocks/LatestPosts';
import type { ResourceCardProps } from '@/components/common/Card/ResourceCard';
import type { BlockWithAudience } from '@/types/shared';

import type { ArticleGrid as SanityArticleGrid, Audience as SanityAudience } from '@/sanity/sanity.types'

export interface ArticleGridProps
  extends Omit<SanityArticleGrid, 'articles'>,
  BlockWithAudience {
  articles?: Array<
    Omit<ResourceCardProps, 'audienceRef'> & {
      audienceRef?: SanityAudience;
    }
  >;
  latestPostsData?: LatestPostsDataProps;
}

export const ArticleGrid = ({
  heading,
  description,
  articles,
  loadMoreTrackingTag,
  articleTheme,
  initialRows,
  category,
  latestPostsData,
  backgroundColour,
  blockSpacing,
  paddingAdjustment,
  pageAudience,
  isAudienceSwitcherEnabled,
}: ArticleGridProps) => {
  const getBlogData = () => {
    if (category) {
      const { postsPerCategory } = latestPostsData;
      const reducedCategoryPosts = postsPerCategory.filter(
        (p) => p._id === category._ref,
      )[0]?.blogPosts;

      return reducedCategoryPosts.map((post) => ({
        blogAuthorName: post.author.name,
        authorImage: post.author.image,
        blogAuthorLink: `/blog/author/${post.author.slug?.current}`,
        blogPostPublishDate: formatDate(post?.publishDate),
        heading: blocksToText(post.heading),
        body: post.excerpt,
        trackingTag: {
          blockName: 'blogPost',
          blockAction: 'linkClick',
        },
        url: `/blog/${post.slug.current}`,
      }));
    }
    return null;
  };

  const items = getBlogData() || articles || [];

  const finalItems = items.map((item) => (
    <ResourceCard
      key={item?._id}
      {...item}
      colourOverride={articleTheme}
      pageAudience={pageAudience}
      isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
    />
  ));

  return (
    <Section
      verticalPadding={paddingAdjustment ?? true}
      spacing={blockSpacing}
      backgroundColour={backgroundColour?.token}
      className="article-grid"
    >
      <HeadingLevel>
        <StyledCaptionHeadingDescription
          heading={heading}
          description={description}
          defaultHeadingSize="subheading-lg"
          padding={{ description: 'xx-small', descriptionMobile: 'xx-small' }}
        />
        {items && (
          <ArticleGridSytem
            initialRows={
              initialRows && {
                sm: initialRows?.mobile || 1,
                md: initialRows?.tablet || 1,
                lg: initialRows?.desktop || 1,
                xl: initialRows?.desktop || 1,
                max: initialRows?.desktop || 1,
              }
            }
            loadMoreRowIncrements={{ sm: 3, md: 2, lg: 2, xl: 2, max: 2 }}
            items={finalItems}
            loadMoreTrackingTag={
              loadMoreTrackingTag
                ? {
                  _type: 'loadMoreTrackingTag',
                  blockName: loadMoreTrackingTag.blockName ?? '',
                  blockAction: loadMoreTrackingTag.blockAction ?? '',
                }
                : undefined
            }
          />
        )}
      </HeadingLevel>
    </Section>
  );
};

const StyledCaptionHeadingDescription = styled(CaptionHeadingDescription)`
  margin-bottom: var(--spacing-large);
`;
